<template>
  <div
    class="ally-modal__backdrop"
    role="presentation"
    @click.self="handleClose"
  >
    <article
      ref="dialogRef"
      class="ally-modal"
      role="dialog"
      :aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown="handleKeydown"
    >
      <header class="ally-modal__header">
        <div class="ally-modal__heading">
          <h2 :id="titleId">
            {{ ally?.name }}
          </h2>
          <p v-if="categoryLabel" class="ally-modal__category">
            {{ categoryLabel }}
          </p>
        </div>

        <div class="ally-modal__header-actions">
          <button
            type="button"
            class="ally-modal__icon-button"
            :aria-label="t('ally_modal.share_aria', { name: ally?.name || '' })"
            @click="handleShare"
          >
            <i class="fas fa-share-alt" aria-hidden="true" />
            <span class="sr-only">{{ t("ally_modal.share") }}</span>
          </button>
          <button
            type="button"
            class="ally-modal__icon-button ally-modal__icon-button--close"
            :aria-label="t('ally_modal.close')"
            @click="handleClose"
          >
            <i class="fas fa-times" aria-hidden="true" />
            <span class="sr-only">{{ t("ally_modal.close") }}</span>
          </button>
        </div>
      </header>

      <section class="ally-modal__body">
        <div class="ally-modal__media">
          <div class="ally-modal__slider">
            <Swiper
              v-if="gallery.length"
              :loop="shouldLoop"
              :navigation="true"
              :autoplay="{ delay: 3000, disableOnInteraction: false }"
              class="ally-modal__swiper"
            >
              <SwiperSlide v-for="(image, index) in gallery" :key="index">
                <img
                  :src="image"
                  :alt="t('ally_modal.gallery_alt', { index: index + 1, name: ally?.name || '' })"
                  class="ally-modal__slide-image"
                  @load="handleGalleryImageLoad($event, image, index)"
                />
              </SwiperSlide>
            </Swiper>

            <div
              v-else
              class="ally-modal__slider-placeholder"
            >
              <span>{{ t("ally_modal.no_images") }}</span>
            </div>
          </div>

          <div class="ally-modal__status">
            <span v-if="!inForce" class="ally-modal__badge">
              {{ t("ally_modal.badge_out_of_force") }}
            </span>

            <span v-if="validityRange" class="ally-modal__validity" :title="validityTooltip">
              <i class="far fa-calendar-alt" aria-hidden="true" />
              {{ validityRange }}
            </span>
          </div>
        </div>

        <div class="ally-modal__details">
          <div class="ally-modal__contact">
            <a
              v-if="ally?.whatsapp"
              class="ally-modal__contact-button"
              :href="`https://wa.me/${ally.whatsapp}`"
              target="_blank"
              rel="noopener noreferrer"
              @click.prevent="handleWhatsappClick(ally.whatsapp)"
            >
              <i class="fab fa-whatsapp" aria-hidden="true" />
              {{ t("ally_modal.contact_whatsapp") }}
            </a>

            <a
              v-if="ally?.phone"
              class="ally-modal__contact-button"
              :href="`tel:${ally.phone}`"
              @click.prevent="handlePhoneClick(ally.phone)"
            >
              <i class="fas fa-phone-alt" aria-hidden="true" />
              {{ ally.phone }}
            </a>

            <a
              v-if="hasMap"
              class="ally-modal__contact-button"
              :href="mapLink"
              target="_blank"
              rel="noopener noreferrer"
              @click.prevent="handleMapClick"
            >
              <i class="fas fa-map-marker-alt" aria-hidden="true" />
              {{ t("ally_modal.open_map") }}
            </a>

            <a
              v-if="ally?.more_info_url"
              class="ally-modal__contact-button"
              :href="ally.more_info_url"
              target="_blank"
              rel="noopener noreferrer"
              @click.prevent="handleMoreInfoClick"
            >
              <i class="fas fa-info-circle" aria-hidden="true" />
              {{ t("ally_modal.more_info") }}
            </a>
          </div>

          <div class="ally-modal__benefit">
            <h3 class="ally-modal__section-title">
              {{ ally?.benefit_title || t("ally_modal.benefit_title") }}
            </h3>
            <p v-if="ally?.benefit_description" class="ally-modal__paragraph">
              {{ ally.benefit_description }}
            </p>
            <ul v-if="benefitConditions.length" class="ally-modal__list">
              <li v-for="(condition, index) in benefitConditions" :key="index">
                {{ condition }}
              </li>
            </ul>

            <button
              type="button"
              class="ally-modal__cta"
              :disabled="!canRedeem"
              :title="redeemTooltip"
              @click="handleRedeem"
            >
              {{ redeemLabel }}
            </button>

            <p
              v-if="!isAuthenticated && canShowLoginHint"
              class="ally-modal__hint"
            >
              {{ t("ally_modal.login_hint") }}
            </p>
          </div>
        </div>
      </section>

      <footer v-if="showForm" class="ally-modal__footer">
        <BenefitForm
          :ally="ally"
          :prefill-user="prefillUser"
          @submitted="handleSubmitted"
          @close="handleClose"
          @auth-required="handleAuthRequired"
        />
      </footer>
    </article>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Autoplay, Navigation, Pagination } from "swiper";
import BenefitForm from "@/components/alliances/BenefitForm.vue";
import { useAuth } from "@/composables/useAuth";
import { isAllyInForce } from "@/composables/useAllies";
import { trackAllyEvent } from "@/api/metrics";
import "swiper/swiper-bundle.css";

SwiperCore.use([Navigation, Pagination, Autoplay]);

const TARGET_ASPECT_RATIO = 16 / 9;
const ASPECT_TOLERANCE = 0.02;

const props = defineProps({
  ally: {
    type: Object,
    required: true,
  },
  prefillUser: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close", "share", "submitted", "auth-required"]);

const { t } = useI18n();
const { isAuthenticated, requireLogin } = useAuth();

const dialogRef = ref(null);
const showForm = ref(false);
const previouslyFocused = ref(null);
const imageAspectMap = ref({});

const createDomId = () => `ally-modal-title-${Math.random().toString(36).slice(2, 8)}`;

const titleId = createDomId();

const inForce = computed(() => isAllyInForce(props.ally));

const gallery = computed(() => {
  const images = props.ally?.gallery || props.ally?.images || [];
  return images
    .map((item) => item?.url || item?.path)
    .filter(Boolean);
});

const shouldLoop = computed(() => gallery.value.length > 1);

const resetImageAspectMap = () => {
  imageAspectMap.value = {};
};

const updateLetterboxForRenderedImages = () => {
  nextTick(() => {
    const dialog = dialogRef.value;
    if (!dialog) {
      return;
    }
    const nodes = dialog.querySelectorAll(".ally-modal__swiper img");
    if (!nodes.length) {
      return;
    }
    const updated = { ...imageAspectMap.value };
    nodes.forEach((img, index) => {
      if (!img.complete || !img.naturalWidth || !img.naturalHeight) {
        return;
      }
      const key = getGalleryImageKey(index);
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      updated[key] = aspectRatio < TARGET_ASPECT_RATIO - ASPECT_TOLERANCE;
    });
    imageAspectMap.value = updated;
  });
};

watch(
  () => props.ally?.id,
  () => {
    resetImageAspectMap();
    updateLetterboxForRenderedImages();
  },
  { immediate: true }
);

watch(
  () => gallery.value,
  () => {
    resetImageAspectMap();
    updateLetterboxForRenderedImages();
  },
  { deep: true }
);

const getGalleryImageKey = (index) => `index-${index}`;

const handleGalleryImageLoad = (event, _image, index) => {
  const target = event?.target;
  if (!target?.naturalWidth || !target?.naturalHeight) {
    return;
  }
  const aspectRatio = target.naturalWidth / target.naturalHeight;
  const key = getGalleryImageKey(index);
  const shouldLetterbox = aspectRatio < TARGET_ASPECT_RATIO - ASPECT_TOLERANCE;
  imageAspectMap.value = {
    ...imageAspectMap.value,
    [key]: shouldLetterbox,
  };
};

const shouldLetterboxImage = (_image, index) => {
  const key = getGalleryImageKey(index);
  return Boolean(imageAspectMap.value[key]);
};

onMounted(() => {
  updateLetterboxForRenderedImages();
});

const categoryLabel = computed(() => {
  const category = props.ally?.category;
  const subCategory = props.ally?.sub_category || props.ally?.subCategory;
  if (category && subCategory) {
    return `${category?.name || ""} · ${subCategory?.name || ""}`;
  }
  if (category) {
    return category?.name || category?.label || "";
  }
  return "";
});

const benefitConditions = computed(() => {
  const conditions = props.ally?.benefit_conditions;
  if (Array.isArray(conditions)) {
    return conditions.filter(Boolean);
  }
  if (typeof conditions === "string") {
    return conditions
      .split(/\n|·|-|\*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
});

const hasMap = computed(
  () => Boolean(props.ally?.latitude) && Boolean(props.ally?.longitude)
);

const mapLink = computed(() => {
  if (!hasMap.value) {
    return "#";
  }
  return `https://www.google.com/maps?q=${props.ally.latitude},${props.ally.longitude}`;
});

const validityTooltip = computed(() => {
  if (!props.ally?.valid_from && !props.ally?.valid_to) {
    return "";
  }
  const formatter = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const from = props.ally.valid_from ? formatter.format(new Date(props.ally.valid_from)) : null;
  const to = props.ally.valid_to ? formatter.format(new Date(props.ally.valid_to)) : null;
  if (from && to) {
    return t("ally_modal.validity_range", { from, to });
  }
  if (from) {
    return t("ally_modal.validity_from", { from });
  }
  if (to) {
    return t("ally_modal.validity_to", { to });
  }
  return "";
});

const validityRange = computed(() => {
  if (!validityTooltip.value) {
    return "";
  }
  return validityTooltip.value;
});

const canRedeem = computed(() => inForce.value && isAuthenticated.value);

const redeemTooltip = computed(() => {
  if (inForce.value && isAuthenticated.value) {
    return "";
  }
  if (!inForce.value) {
    return validityTooltip.value || t("ally_modal.disabled_expired");
  }
  return t("ally_modal.disabled_login");
});

const redeemLabel = computed(() => {
  if (!inForce.value) {
    return t("ally_modal.redeem_disabled");
  }
  if (!isAuthenticated.value) {
    return t("ally_modal.redeem_login");
  }
  return t("ally_modal.redeem");
});

const canShowLoginHint = computed(() => !showForm.value && !isAuthenticated.value);

const restoreFocus = () => {
  if (previouslyFocused.value && typeof previouslyFocused.value.focus === "function") {
    previouslyFocused.value.focus();
  }
};

const focusFirstElement = () => {
  nextTick(() => {
    const dialog = dialogRef.value;
    if (!dialog) {
      return;
    }
    const focusable = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable?.[0];
    if (first && typeof first.focus === "function") {
      first.focus();
    } else {
      dialog.focus();
    }
  });
};

const trapFocus = (event) => {
  if (typeof document === "undefined") {
    return;
  }

  const dialog = dialogRef.value;
  if (!dialog) {
    return;
  }
  const focusableItems = dialog.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  if (!focusableItems.length) {
    event.preventDefault();
    return;
  }

  const firstElement = focusableItems[0];
  const lastElement = focusableItems[focusableItems.length - 1];

  const { activeElement } = document;

  if (event.shiftKey) {
    if (activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }
  } else if (activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

const handleKeydown = (event) => {
  if (event.key === "Escape") {
    event.preventDefault();
    handleClose();
  } else if (event.key === "Tab") {
    trapFocus(event);
  }
};

const handleClose = () => {
  showForm.value = false;
  emit("close");
  restoreFocus();
};

const handleShare = () => {
  emit("share", props.ally);
};

const openUrl = async (url, callback) => {
  if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  await callback();
};

const handleWhatsappClick = async (phone) => {
  await openUrl(`https://wa.me/${phone}`, async () => {
    await trackAllyEvent(props.ally.id, "ally_click_whatsapp", {
      phone,
    });
  });
};

const handlePhoneClick = async (phone) => {
  if (typeof window !== "undefined") {
    window.location.href = `tel:${phone}`;
  }
};

const handleMapClick = async () => {
  await openUrl(mapLink.value, async () => {
    await trackAllyEvent(props.ally.id, "ally_click_map", {
      latitude: props.ally.latitude,
      longitude: props.ally.longitude,
    });
  });
};

const handleMoreInfoClick = async () => {
  await openUrl(props.ally.more_info_url, async () => {
    await trackAllyEvent(props.ally.id, "ally_click_more_info", {
      destination: props.ally.more_info_url,
    });
  });
};

const handleRedeem = () => {
  if (!inForce.value) {
    return;
  }
  if (!isAuthenticated.value) {
    requireLogin();
    emit("auth-required");
    return;
  }
  showForm.value = true;
  nextTick(() => {
    const firstInput = dialogRef.value?.querySelector("input");
    if (firstInput) {
      firstInput.focus();
    }
  });
};

const handleSubmitted = (payload) => {
  emit("submitted", payload);
};

const handleAuthRequired = () => {
  emit("auth-required");
};

onMounted(async () => {
  if (typeof document !== "undefined") {
    previouslyFocused.value = document.activeElement || null;
  }
  focusFirstElement();
});

onBeforeUnmount(() => {
  restoreFocus();
});

watch(
  () => props.ally?.id,
  () => {
    showForm.value = false;
  }
);
</script>

<style scoped>
.ally-modal__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  z-index: 999;
}

.ally-modal {
  width: min(960px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  outline: none;
}

.ally-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 28px 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.ally-modal__heading h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #111827;
}

.ally-modal__category {
  margin: 4px 0 0;
  font-size: 0.95rem;
  color: #6b7280;
}

.ally-modal__header-actions {
  display: flex;
  gap: 8px;
}

.ally-modal__icon-button {
  border: none;
  background: #f9fafb;
  color: #111827;
  border-radius: 999px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.ally-modal__icon-button--close {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}

.ally-modal__icon-button:hover {
  background: rgba(249, 115, 22, 0.12);
  transform: translateY(-1px);
}

.ally-modal__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px 28px;
}

.ally-modal__media {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ally-modal__slider {
  margin: -24px -28px 16px;
  background: #000;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
  position: relative;
}

.ally-modal__swiper {
  width: 100%;
  background: #000;
  min-height: 35vh;
}

.ally-modal__slider-placeholder {
  min-height: 35vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #111;
}

:deep(.ally-modal__swiper .swiper-wrapper) {
  height: 35vh;
  align-items: center;
}

:deep(.ally-modal__swiper .swiper-slide) {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

:deep(.ally-modal__swiper img) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

:deep(.ally-modal__swiper .swiper-button-prev),
:deep(.ally-modal__swiper .swiper-button-next) {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
}

:deep(.ally-modal__slider:hover .swiper-button-prev),
:deep(.ally-modal__slider:hover .swiper-button-next),
:deep(.ally-modal__swiper .swiper-button-prev:focus),
:deep(.ally-modal__swiper .swiper-button-next:focus) {
  opacity: 1;
  pointer-events: auto;
}

@media (hover: none) and (pointer: coarse) {
  :deep(.ally-modal__swiper .swiper-button-prev),
  :deep(.ally-modal__swiper .swiper-button-next) {
    opacity: 1;
    pointer-events: auto;
  }
}

.ally-modal__status {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.ally-modal__badge {
  background: rgba(239, 68, 68, 0.15);
  color: #b91c1c;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ally-modal__validity {
  color: #4b5563;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ally-modal__details {
  display: grid;
  gap: 24px;
}

.ally-modal__contact {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ally-modal__contact-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;
}

.ally-modal__contact-button:hover {
  background: rgba(249, 115, 22, 0.2);
  transform: translateY(-1px);
}

.ally-modal__benefit {
  display: grid;
  gap: 12px;
}

.ally-modal__section-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #111827;
}

.ally-modal__paragraph {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

.ally-modal__list {
  margin: 0;
  padding-left: 18px;
  color: #374151;
}

.ally-modal__cta {
  align-self: start;
  background: linear-gradient(135deg, #f97316, #ec4899);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  padding: 12px 32px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.ally-modal__cta:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.ally-modal__cta:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 22px rgba(236, 72, 153, 0.35);
}

.ally-modal__hint {
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
}

.ally-modal__footer {
  padding: 24px 28px 32px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: #f9fafb;
  border-radius: 0 0 24px 24px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (min-width: 900px) {
  .ally-modal__body {
    grid-template-columns: 1.1fr 0.9fr;
  }

  .ally-modal__media {
    position: sticky;
    top: 0;
  }
}

@media (max-width: 640px) {
  .ally-modal__backdrop {
    padding: 16px;
  }

  .ally-modal__header,
  .ally-modal__body,
  .ally-modal__footer {
    padding: 20px;
  }

  .ally-modal__heading h2 {
    font-size: 1.45rem;
  }
}
</style>
